import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, RotateCw, PenTool, Radio, LineChart } from 'lucide-react';
import { useT } from '@/lib/i18n/useT';
import { cn } from '@/lib/cn';
import { useAiAssistantStore } from './aiAssistantStore';
import { useAIContext } from './useAIContext';
import { useAIStream } from './useAIStream';
import { ChatMessage } from './ChatMessage';
import { SuggestedChips } from './SuggestedChips';
import { ChatComposer } from './ChatComposer';

/**
 * Maps the backend's error code (sent via SSE `event: error`) to a friendly
 * translation key. Unknown codes fall back to the generic message.
 */
function errorKeyFor(code: string | null): string {
  if (!code) return 'assistant.error';
  if (code === 'assistant_unavailable') return 'assistant.error.unavailable';
  return 'assistant.error';
}

interface AssistantChatProps {
  /** Whether the sidebar is open. Drives the lazy kickoff trigger. */
  open: boolean;
}

/** ATLEDI's three modes (build-v2 G8) — each reframes the assistant and offers
 * its own quick-starts: Designer (plan/setup), Field (live/ops), Analyst
 * (measure/recap). Faithful to the prototype's Designer/Field/Analyst voices. */
type AtlediMode = 'designer' | 'field' | 'analyst';
const ATLEDI_MODES: {
  id: AtlediMode;
  label: string;
  icon: React.ReactNode;
  color: string;
  quickStarts: string[];
}[] = [
  {
    id: 'designer',
    label: 'Designer',
    icon: <PenTool className="h-3.5 w-3.5" />,
    color: '#B262F5',
    quickStarts: [
      'Ayúdame a planear un proyecto de sampling y su objetivo',
      '¿Qué assets por Purpose (SEE/ENGAGE/TRY/ACT) necesito?',
      'Sugiere la estructura de grupos y eventos para esta marca',
    ],
  },
  {
    id: 'field',
    label: 'Field',
    icon: <Radio className="h-3.5 w-3.5" />,
    color: '#4dd28b',
    quickStarts: [
      '¿Qué eventos están live ahora y cómo va el pace?',
      'Detecta conflictos de horario en el roster',
      '¿Qué eventos tienen gaps de staffing o stations?',
    ],
  },
  {
    id: 'analyst',
    label: 'Analyst',
    icon: <LineChart className="h-3.5 w-3.5" />,
    color: '#5aa9ff',
    quickStarts: [
      'Resume el MENT y el trial-funnel del proyecto',
      'Redacta el recap deck para el cliente',
      '¿Qué dice la validación de stakeholders y el DiD?',
    ],
  },
];

/**
 * The chat shell. The kickoff effect is the critical cost-control gate:
 *  - if the sidebar is closed, no fetch ever fires
 *  - if the user reopens on the same context, the existing thread is reused
 *  - if the context fingerprint changes, the thread is wiped and a fresh
 *    kickoff is sent the next time the sidebar is open
 */
export function AssistantChat({ open }: AssistantChatProps) {
  const t = useT();
  const ctx = useAIContext();
  const messages = useAiAssistantStore((s) => s.messages);
  const chips = useAiAssistantStore((s) => s.chips);
  const isStreaming = useAiAssistantStore((s) => s.isStreaming);
  const error = useAiAssistantStore((s) => s.error);
  const clearError = useAiAssistantStore((s) => s.clearError);
  const resetIfContextChanged = useAiAssistantStore(
    (s) => s.resetIfContextChanged,
  );
  const { send } = useAIStream();
  const [mode, setMode] = useState<AtlediMode>('designer');
  const activeMode = ATLEDI_MODES.find((m) => m.id === mode)!;

  const handleRetry = () => {
    clearError();
    void send({ ctx });
  };

  // Reset thread when the user navigates to a different context.
  useEffect(() => {
    resetIfContextChanged(ctx.fingerprint);
  }, [ctx.fingerprint, resetIfContextChanged]);

  // Lazy kickoff: only when the sidebar is open AND the thread is empty.
  useEffect(() => {
    if (!open) return;
    if (messages.length > 0 || isStreaming) return;
    void send({ ctx });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ctx.fingerprint]);

  // Auto-scroll to bottom on new content.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, chips, isStreaming]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* ATLEDI mode selector (G8) */}
      <div className="flex flex-none gap-1 border-b border-[var(--color-xk-border)] px-2 py-1.5">
        {ATLEDI_MODES.map((mdef) => (
          <button
            key={mdef.id}
            type="button"
            onClick={() => setMode(mdef.id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-[11px] font-bold transition-colors',
              mode === mdef.id ? 'text-white' : 'text-[var(--color-xk-text-muted)] hover:text-[var(--color-xk-lavender)]',
            )}
            style={mode === mdef.id ? { background: mdef.color } : undefined}
          >
            {mdef.icon}
            {mdef.label}
          </button>
        ))}
      </div>
      <div
        ref={scrollerRef}
        className="scrollbar-thin flex-1 overflow-y-auto"
      >
        {messages.length === 0 && !isStreaming && !error && (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <p className="text-xs text-[var(--color-xk-text-muted)]">
              {t('assistant.kickoffPending')}
            </p>
          </div>
        )}
        {messages.map((m) => (
          <ChatMessage key={m.id} message={m} />
        ))}
        {error && (
          <div className="m-3 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12px] text-red-300">
            <AlertTriangle className="mt-[2px] h-3.5 w-3.5 flex-none" />
            <div className="min-w-0 flex-1">
              <p>{t(errorKeyFor(error))}</p>
              <button
                type="button"
                onClick={handleRetry}
                disabled={isStreaming}
                className="mt-1.5 inline-flex items-center gap-1 rounded-sm text-[11px] font-semibold text-red-200 underline decoration-dotted underline-offset-2 hover:text-white disabled:opacity-50"
              >
                <RotateCw className="h-3 w-3" />
                {t('assistant.retry')}
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Mode quick-starts (G8) — tailored prompts for the active ATLEDI mode */}
      <div className="flex flex-none flex-col gap-1 border-t border-[var(--color-xk-border)] p-2">
        <span className="mono px-1 text-[9px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
          {activeMode.label} · quick starts
        </span>
        {activeMode.quickStarts.map((q) => (
          <button
            key={q}
            type="button"
            disabled={isStreaming}
            onClick={() => send({ ctx, userMessage: q })}
            className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-2.5 py-1.5 text-left text-[11px] text-[var(--color-xk-text-muted)] transition-colors hover:border-[var(--color-xk-accent)] hover:text-[var(--color-xk-lavender)] disabled:opacity-50"
          >
            {q}
          </button>
        ))}
      </div>
      <SuggestedChips
        chips={chips}
        contextKind={ctx.kind}
        disabled={isStreaming}
        onPick={(text) => send({ ctx, userMessage: text })}
      />
      <ChatComposer
        disabled={isStreaming}
        onSend={(text) => send({ ctx, userMessage: text })}
      />
    </div>
  );
}
